import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billingService } from '../services/billing';
import { useAuthStore } from '../stores/authStore';

export const billingKeys = {
  all: ['billing'] as const,
  subscription: () => [...billingKeys.all, 'subscription'] as const,
  config: () => [...billingKeys.all, 'config'] as const,
};

/** Current user's subscription/entitlement status. */
export function useSubscription() {
  return useQuery({
    queryKey: billingKeys.subscription(),
    queryFn: () => billingService.getSubscription(),
  });
}

/**
 * Whether the current user has Pro access. True for an active/trialing subscription
 * OR for admins (who bypass gating on the backend too). `isLoading` lets callers
 * avoid flashing an upgrade prompt before the subscription status has loaded.
 */
export function useIsPro(): { isPro: boolean; isLoading: boolean } {
  const { data, isLoading } = useSubscription();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  return { isPro: isAdmin || Boolean(data?.active), isLoading: isLoading && !isAdmin };
}

/** Public Paddle config needed to open checkout. */
export function useBillingConfig() {
  return useQuery({
    queryKey: billingKeys.config(),
    queryFn: () => billingService.getConfig(),
    // Refetch periodically so a client token cached across an environment change
    // (e.g. sandbox → production) self-heals instead of sticking for the session.
    staleTime: 5 * 60 * 1000,
  });
}

/** Cancel the subscription at period end. */
export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => billingService.cancelSubscription(),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: billingKeys.subscription() }),
  });
}
